import { useCallback } from 'react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { App } from 'antd';
import type { UploadFile } from 'antd';
import {
  gStaffFileListAtom,
  gStaffPreviewAtom,
  gStaffStagedKeyAtom,
  gStaffUploadingAtom,
  gStaffValidatingAtom,
} from '../atoms/gStaffUploadAtoms';
import {
  createWorkHistoryUploadUrl,
  putWorkHistoryFileToS3,
  uploadWorkHistoryFile,
  validateWorkHistoryFile,
  type WorkHistoryPreview,
} from '../api/gStaffMutations';
import { guardedClient } from '../../auth/api/clients';
import { HttpError } from '../../../shared/errors/HttpError';
import { extractProblemDetail } from '../../../shared/errors/problemDetail';

// 転送経路（S3 直 PUT）には実質上限がないため、これはバックエンド変換側の制約。
// exceljs の展開メモリ（Lambda 2048MB、cdk/lib/like-backend-stack.ts 参照）で処理できる目安。
// 約12万行相当で、超える規模になったらバックエンドのストリーミング化とあわせて見直すこと。
// サーバー側の強制値（backend service の MAX_WORK_HISTORY_FILE_SIZE_BYTES）と一致させること
const MAX_UPLOAD_FILE_SIZE_MB = 10;
const MAX_UPLOAD_FILE_SIZE_BYTES = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;

interface GStaffUpload {
  fileList: UploadFile[];
  /** 選択中ファイルのサーバー検証結果。未検証・ファイル未選択時は null */
  preview: WorkHistoryPreview | null;
  validating: boolean;
  uploading: boolean;
  /** ファイル選択・除去を反映する。選択時は S3 へアップロードしてサーバー検証し、プレビューを更新する */
  changeFile: (fileList: UploadFile[]) => Promise<void>;
  /** 検証済みのステージングファイルを反映し、Gスタッフ稼働歴を置き換える */
  confirmUpload: () => Promise<void>;
}

/** Gスタッフ稼働歴ファイルの「選択 → 検証プレビュー → 反映」の状態と操作を提供する。 */
export const useGStaffUpload = (): GStaffUpload => {
  const { message } = App.useApp();
  const fileList = useAtomValue(gStaffFileListAtom);
  const preview = useAtomValue(gStaffPreviewAtom);
  const validating = useAtomValue(gStaffValidatingAtom);
  const uploading = useAtomValue(gStaffUploadingAtom);

  // useAtomCallback により、再レンダリング（UI 無効化）前の操作も最新の送信中フラグで弾く
  const changeFile = useAtomCallback(
    useCallback(
      async (get, set, next: UploadFile[]) => {
        if (get(gStaffValidatingAtom) || get(gStaffUploadingAtom)) return;
        set(gStaffFileListAtom, next);
        set(gStaffPreviewAtom, null);
        set(gStaffStagedKeyAtom, null);
        const file = next[next.length - 1]?.originFileObj;
        if (file === undefined) return;
        if (file.size > MAX_UPLOAD_FILE_SIZE_BYTES) {
          set(gStaffFileListAtom, []);
          void message.error(
            `ファイルサイズが上限（${String(MAX_UPLOAD_FILE_SIZE_MB)}MB）を超えているためアップロードできません`
          );
          return;
        }
        set(gStaffValidatingAtom, true);
        try {
          const { url, key } = await createWorkHistoryUploadUrl(guardedClient);
          await putWorkHistoryFileToS3(url, file);
          const result = await validateWorkHistoryFile(guardedClient, key);
          set(gStaffStagedKeyAtom, key);
          set(gStaffPreviewAtom, result);
        } catch (error) {
          set(gStaffFileListAtom, []);
          void message.error(extractProblemDetail(error) ?? '稼働歴ファイルの検証に失敗しました');
        } finally {
          set(gStaffValidatingAtom, false);
        }
      },
      [message]
    )
  );

  const confirmUpload = useAtomCallback(
    useCallback(
      async (get, set) => {
        if (get(gStaffValidatingAtom) || get(gStaffUploadingAtom)) return;
        const key = get(gStaffStagedKeyAtom);
        const currentPreview = get(gStaffPreviewAtom);
        // 検証を通過していない（キー未取得・取込対象 0 件の）ファイルは反映させない
        if (key === null || currentPreview === null || currentPreview.recordCount === 0) return;
        set(gStaffUploadingAtom, true);
        try {
          const { recordCount } = await uploadWorkHistoryFile(guardedClient, key);
          void message.success(`Gスタッフ稼働歴を更新しました（取込 ${String(recordCount)}件）`);
          set(gStaffFileListAtom, []);
          set(gStaffPreviewAtom, null);
          set(gStaffStagedKeyAtom, null);
        } catch (error) {
          // ステージングの期限切れ（404）は同じキーでの再試行が無意味なため、状態を破棄して再選択させる
          if (error instanceof HttpError && error.status === 404) {
            set(gStaffFileListAtom, []);
            set(gStaffPreviewAtom, null);
            set(gStaffStagedKeyAtom, null);
          }
          void message.error(extractProblemDetail(error) ?? '稼働歴ファイルの反映に失敗しました');
        } finally {
          set(gStaffUploadingAtom, false);
        }
      },
      [message]
    )
  );

  return { fileList, preview, validating, uploading, changeFile, confirmUpload };
};
